import { Asset, AssetGroup } from "../models/asset";

export interface IAssetGroup {
    id: string;
    name: string;
    assetsCount: number;
}

export interface IAsset {
    id: string;
    name: string;
    vehicleType: string;
}

const assetMatchesVehicleSearch = (asset: Asset, normalizedSearchTerm: string) =>
    asset.name?.toLowerCase().includes(normalizedSearchTerm) ||
    asset.chassis?.toLowerCase().includes(normalizedSearchTerm);

export const getAssetsByGroup = (
    allAssets: Asset[],
    groupId: string,
    searchTerm = "",
    viewMode: "assets" | "groups" = "assets"
): IAsset[] => {
    const normalizedSearchTerm = searchTerm.toLowerCase();

    return allAssets
        .filter((asset) => asset.groupIds?.includes(groupId))
        .filter((asset) => !searchTerm || viewMode !== "assets" || assetMatchesVehicleSearch(asset, normalizedSearchTerm))
        .map((asset) => ({
            id: asset.id,
            name: asset.name,
            vehicleType: asset.type ?? "car",
        }));
};

export const getAllAssetIds = (allAssets: Asset[]): string[] => {
    return allAssets.map((asset) => asset.id);
};

export const getAssetGroups = (
    allAssets: Asset[],
    allGroups: AssetGroup[],
    searchTerm: string,
    type: "assets" | "groups",
    limit: number,
    offset: number
): { groups: IAssetGroup[], hasMore: boolean } => {
    const normalizedSearchTerm = searchTerm.toLowerCase();

    if (type === "assets") {
        const filteredAssets = allAssets.filter(
            (asset) =>
                asset.name?.toLowerCase().includes(normalizedSearchTerm) ||
                asset.chassis?.toLowerCase().includes(normalizedSearchTerm)
        );

        const assetGroups = allGroups.filter((group) =>
            filteredAssets.some((asset) => asset.groupIds?.includes(group.id))
        );

        return {
            groups: assetGroups
                .map((group) => ({
                    id: group.id,
                    name: group.name,
                    assetsCount: filteredAssets.filter((asset) =>
                        asset.groupIds?.includes(group.id)
                    ).length,
                }))
                .slice(offset, offset + limit),
            hasMore: offset + limit < assetGroups.length
        }
    }

    const filteredGroups = allGroups.filter((group) =>
        group.name.toLowerCase().includes(normalizedSearchTerm)
    );

    return {
        groups: filteredGroups
            .map((group) => ({
                id: group.id,
                name: group.name,
                assetsCount: getAssetsByGroup(allAssets, group.id).length,
            }))
            .slice(offset, offset + limit),
        hasMore: offset + limit < filteredGroups.length
    }
};

export const getFilteredAssetIds = (
    allAssets: Asset[],
    allGroups: AssetGroup[],
    searchTerm: string,
    viewMode: "assets" | "groups"
): string[] => {
    if (!searchTerm) {
        return getAllAssetIds(allAssets);
    }

    const normalizedSearchTerm = searchTerm.toLowerCase();

    if (viewMode === "assets") {
        return allAssets
            .filter((asset) => assetMatchesVehicleSearch(asset, normalizedSearchTerm))
            .map((asset) => asset.id);
    }

    const matchingGroupIds = new Set(
        allGroups
            .filter((group) => group.name.toLowerCase().includes(normalizedSearchTerm))
            .map((group) => group.id)
    );

    return allAssets
        .filter((asset) => asset.groupIds?.some((groupId) => matchingGroupIds.has(groupId)))
        .map((asset) => asset.id);
};

export const buildAssetById = (allAssets: Asset[]): Map<string, Asset> => {
    const assetById = new Map<string, Asset>();

    for (const asset of allAssets) {
        assetById.set(asset.id, asset);
    }

    return assetById;
};

export const filterSelectedAssetIdsBySearch = (
    assetById: Map<string, Asset>,
    allGroups: AssetGroup[],
    selectedAssetIds: Set<string>,
    searchTerm: string,
    viewMode: "assets" | "groups"
): Set<string> => {
    if (!searchTerm) {
        return selectedAssetIds;
    }

    const normalizedSearchTerm = searchTerm.toLowerCase();
    const matchingSelectedAssetIds = new Set<string>();

    if (viewMode === "assets") {
        for (const assetId of selectedAssetIds) {
            const asset = assetById.get(assetId);
            if (!asset) {
                continue;
            }

            const matchesSearch =
                asset.name?.toLowerCase().includes(normalizedSearchTerm) ||
                asset.chassis?.toLowerCase().includes(normalizedSearchTerm);

            if (matchesSearch) {
                matchingSelectedAssetIds.add(assetId);
            }
        }

        return matchingSelectedAssetIds;
    }

    const matchingGroupIds = new Set(
        allGroups
            .filter((group) => group.name.toLowerCase().includes(normalizedSearchTerm))
            .map((group) => group.id)
    );

    for (const assetId of selectedAssetIds) {
        const asset = assetById.get(assetId);
        if (!asset) {
            continue;
        }

        if (asset.groupIds?.some((groupId) => matchingGroupIds.has(groupId))) {
            matchingSelectedAssetIds.add(assetId);
        }
    }

    return matchingSelectedAssetIds;
};

export const buildAssetTypeById = (allAssets: Asset[]): Map<string, string> => {
    const assetTypeById = new Map<string, string>();

    for (const asset of allAssets) {
        assetTypeById.set(asset.id, asset.type ?? "car");
    }

    return assetTypeById;
};

export const getVehicleTypeCounts = (
    assetTypeById: Map<string, string>,
    selectedAssetIds: Set<string>
): Record<string, number> => {
    const vehicleTypeCounts: Record<string, number> = {};

    for (const assetId of selectedAssetIds) {
        const vehicleType = assetTypeById.get(assetId) ?? "car";
        vehicleTypeCounts[vehicleType] = (vehicleTypeCounts[vehicleType] ?? 0) + 1;
    }

    return vehicleTypeCounts;
};

export const getAllVehicleTypes = (allAssets: Asset[], assetIds: string[]): Record<string, number> => {
    return getVehicleTypeCounts(buildAssetTypeById(allAssets), new Set(assetIds));
};